import dayjs, { Dayjs } from "dayjs";
import { computed } from "vue";
import { useAppStore } from "@/store/app.store";

export function useDateTime() {
  const systemDateTimeFormat = "YYYY-MM-DDTHH:mm:ss";
  const systemDateTimeFormatWithTZ = "YYYY-MM-DDTHH:mm:ssZ";

  const timezones = [
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Phoenix",
    "America/Los_Angeles",
    "America/Anchorage",
  ];

  function getShortTZForDate(date: Date | Dayjs, tz: string | null) {
    if (!tz) return "";

    let locale = "en";
    if (tz.includes("Europe")) locale = "en-GB";
    else if (tz.includes("America")) locale = "en-US";

    const [, tzName] = /.*\s(.+)/.exec(
      date.toLocaleString(locale, {
        timeZone: tz,
        timeZoneName: "short",
      }),
    ) as string[];
    return tzName;
  }

  function getTZNames(tz: string | null): string {
    const startDate = new Date(2000, 1, 1);
    const endDate = new Date(2000, 7, 1);
    return `${getShortTZForDate(startDate, tz)}/${getShortTZForDate(endDate, tz)}`;
  }

  function getLongTZForDate(tz: string): string {
    return `${getTZNames(tz)} (${tz.replace("_", " ")})`;
  }

  function timeAgo(date: string | Date) {
    const diff = dayjs().diff(dayjs(date), "minutes");
    const isPast = diff >= 0;

    if (diff === 0) {
      return "recently";
    }

    const abs = Math.abs(diff);
    const chunks = [];

    const days = Math.floor(abs / (60 * 24));
    if (days !== 0) {
      chunks.push(`${days} ${days === 1 ? "day" : "days"}`);
    }

    const hours = Math.floor((abs % (60 * 24)) / 60);
    if (hours !== 0) {
      chunks.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
    }

    const minutes = abs % 60;
    if (minutes !== 0) {
      chunks.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
    }

    return isPast ? `${chunks.join(" ")} ago` : `In ${chunks.join(" ")} (future)`;
  }

  function dateTZ(dt: string | Date | Dayjs, tz: string | null | undefined, format = "MM/DD/YYYY HH:mm z") {
    if (!dt) return "";
    const dm = tz ? dayjs.utc(dt).tz(tz || undefined) : dayjs.utc(dt);
    return dm ? dm.format(format) : "";
  }

  function showMessageTime(dt: string): string {
    const appStore = useAppStore();
    const timezone = appStore.dispatcher?.timezone;

    if (!timezone) {
      return "";
    }

    const time = dayjs.utc(dt).tz(timezone);

    return time.format(time.isSame(dayjs(), "day") ? "HH:mm z" : "MM/DD/YY HH:mm z");
  }

  function dateToUTC(dt: string | Date, tz: string) {
    return dayjs.tz(dt, tz).utc();
  }

  function dateToUTCSystemFormat(dt: string | Date, tz: string) {
    return dateToUTC(dt, tz).format(systemDateTimeFormat);
  }

  function nowTZ(timezone: string) {
    return dayjs().tz(timezone).format("YYYY-MM-DD HH:mm:ss");
  }

  function fromTZToTZ(dt: string | Date, from: string, to: string, format: string) {
    return dayjs.tz(dt, from).tz(to).format(format);
  }

  function hasTimePassed(dt: string | Date | Dayjs, seconds: number): boolean {
    const diffInSeconds = dayjs().diff(dayjs(dt), "seconds");

    return diffInSeconds > seconds;
  }

  function formatTimeDiff(
    comparedTime: string | Dayjs | Date | null | undefined,
    options?: {
      intervalInSeconds?: number | null;
      exceptionText?: string;
      mode?: "remaining" | "elapsed";
      comparedToTime?: string | Dayjs | Date;
      needSign?: boolean;
      units?: Array<"days" | "hours" | "minutes" | "seconds">;
      format?: "short" | "long";
      showZeros?: boolean;
    },
  ): string {
    const {
      intervalInSeconds = null,
      exceptionText = "",
      mode = "elapsed",
      comparedToTime = dayjs(),
      needSign = true,
      units = ["days", "hours", "minutes"],
      format = "short",
      showZeros = false,
    } = options || {};

    const unitLabel = (value: number, unit: "days" | "hours" | "minutes" | "seconds"): string => {
      if (format === "short") {
        return `${value}${unit[0]}`;
      }

      const singular = unit.slice(0, -1);

      return `${value} ${value === 1 ? singular : unit}`;
    };

    const separator = format === "short" ? ":" : " ";

    if (!comparedTime) return exceptionText;

    const timeOne = dayjs(comparedToTime);
    const timeTwo = dayjs(comparedTime);

    let seconds: number;

    if (mode === "remaining") {
      if (!intervalInSeconds) return exceptionText;
      const elapsed = timeOne.diff(timeTwo, "seconds");
      seconds = intervalInSeconds - elapsed;
    } else {
      seconds = timeTwo.diff(timeOne, "seconds");
    }

    const isNegative = seconds < 0;
    const absSeconds = Math.abs(seconds);

    const totalMinutes = Math.floor(absSeconds / 60);
    const minutes = totalMinutes % 60;

    const totalHours = Math.floor(totalMinutes / 60);
    const hours = totalHours % 24;

    const days = Math.floor(totalHours / 24);

    const sign = isNegative && needSign ? "-" : "";

    const parts: string[] = [];

    if (units.includes("days") && days > 0) parts.push(unitLabel(days, "days"));
    if (units.includes("hours") && (hours > 0 || parts.length > 0)) parts.push(unitLabel(hours, "hours"));
    if (units.includes("minutes") && (minutes > 0 || parts.length > 0)) parts.push(unitLabel(minutes, "minutes"));
    if (units.includes("seconds")) {
      const secs = absSeconds % 60;
      if (secs > 0 || parts.length > 0) {
        parts.push(unitLabel(secs, "seconds"));
      }
    }

    if (parts.length === 0 && showZeros) {
      parts.push(unitLabel(0, "seconds"));
    }

    return sign + parts.join(separator);
  }

  const timezonesList = computed(() =>
    timezones.map((tz) => ({
      id: tz,
      name: getLongTZForDate(tz),
    })),
  );

  const timezonesShortList = computed(() =>
    timezones.map((tz) => ({
      id: tz,
      name: getTZNames(tz),
    })),
  );

  function formatCountdown(targetDate: Dayjs): string {
    const now = dayjs();
    const diffMs = targetDate.diff(now);

    if (diffMs <= 0) {
      return "00s";
    }

    const dur = dayjs.duration(diffMs);
    const days = Math.floor(dur.asDays());
    const hours = dur.hours();
    const minutes = dur.minutes();
    const seconds = dur.seconds();

    const pad = (n: number) => String(n).padStart(2, "0");

    let result = "";
    if (days > 0) result += `${days}d:`;
    if (hours > 0 || days > 0) result += `${pad(hours)}h:`;
    if (minutes > 0 || hours > 0 || days > 0) result += `${pad(minutes)}m:`;
    result += `${pad(seconds)}s`;

    return result;
  }

  function getLocalTimeLabel(date: string, timezone?: string | null) {
    const appStore = useAppStore();

    const dispatcherTimezone = appStore.dispatcher?.timezone;
    const label = "Local time";

    const [_, time] = date.split("T");

    if (dispatcherTimezone && time && timezone && dispatcherTimezone !== timezone) {
      const dispatcherTimezoneName = dayjs().tz(dispatcherTimezone).format("z");
      const dispatcherTimezoneDate = fromTZToTZ(date, timezone, dispatcherTimezone, "HH:mm");

      return label + " / " + dispatcherTimezoneName + ": " + dispatcherTimezoneDate;
    }

    return label;
  }

  function toStartDate(
    date: string | Date | null | undefined,
    tz?: string,
    format = "YYYY-MM-DDTHH:mm:ssZ",
  ): string | null | undefined {
    if (!date) {
      return date;
    }

    const d = tz ? dayjs.tz(date, "YYYY-MM-DD", tz) : dayjs(date);

    if (!d.isValid()) {
      return null;
    }

    return d.startOf("day").format(format);
  }

  function toEndDate(
    date: string | Date | null,
    tz?: string,
    format = "YYYY-MM-DDTHH:mm:ssZ",
  ): string | null | undefined {
    if (!date) {
      return date;
    }

    const d = tz ? dayjs.tz(date, "YYYY-MM-DD", tz) : dayjs(date);

    if (!d.isValid()) {
      return null;
    }

    return d.endOf("day").format(format);
  }

  return {
    systemDateTimeFormat,
    systemDateTimeFormatWithTZ,
    timezones,
    timezonesList: timezonesList.value,
    timezonesShortList: timezonesShortList.value,
    getShortTZForDate,
    getTZNames,
    timeAgo,
    dateTZ,
    showMessageTime,
    dateToUTC,
    dateToUTCSystemFormat,
    nowTZ,
    hasTimePassed,
    formatTimeDiff,
    formatCountdown,
    getLongTZForDate,
    getLocalTimeLabel,
    toStartDate,
    toEndDate,
  };
}
